"use client"

import { HttpTypes } from "@medusajs/types"
import { Container } from "@medusajs/ui"
import Image from "next/image"
import Zoom from "react-medium-image-zoom"
import { useKeenSlider } from "keen-slider/react"
import { useState, useRef } from "react"

import "keen-slider/keen-slider.min.css"
import "react-medium-image-zoom/dist/styles.css"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
  productTitle?: string
}

const ImageGallery = ({ images, productTitle }: ImageGalleryProps) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    mode: "snap",
    slides: {
      perView: 1,
      spacing: 12,
    },
    slideChanged: (slider) => {
      setCurrentSlide(slider.track.details.rel)
    },
  })

  if (!images?.length) return null

  return (
    <div className="relative space-y-4 w-1/2 mx-auto">
      {/* Carrusel principal */}
      <div ref={sliderRef} className="keen-slider flex gap-4">
        {images.map((image, index) =>
          image.url?.trim() ? (
            <div key={image.id} className="keen-slider__slide min-w-[70%]">
              <Container id={image.id} className="relative aspect-[29/34] w-full overflow-hidden bg-ui-bg-subtle">
                <Zoom>
                  <Image
                    src={image.url}
                    priority={index === 0}            // solo la primera con prioridad
                    fetchPriority={index === 0 ? "high" : "auto"} // pista extra para Google
                    alt={index === 0
                      ? productTitle
                        ? `Imagen de ${productTitle}`
                        : "Imagen principal del producto"
                      : `Imagen ${index + 1} de ${productTitle || "producto"}`}
                    className="absolute inset-0 rounded-rounded object-cover"
                    fill
                    sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
                    itemProp={index === 0 ? "image" : undefined} // microseñal semántica
                  />
                </Zoom>
              </Container>
            </div>
          ) : null
        )}
      </div>

      {/* Miniaturas */}
      <div className="flex justify-center gap-2 flex-wrap">
        {images.map((image, index) =>
          image.url?.trim() ? (
            <button
              key={`thumb-${index}`}
              onClick={() => instanceRef.current?.moveToIdx(index)}
              className={`relative w-16 h-16 overflow-hidden border-2 rounded-md ${currentSlide === index
                  ? "border-pink-500"
                  : "border-transparent opacity-70 hover:opacity-100"
                }`}
            >
              <Image src={image.url} alt={`Miniatura ${index + 1} de ${productTitle || "producto"}`} fill className="object-cover" loading="lazy" />

            </button>
          ) : null
        )}
      </div>
    </div>
  )
}

export default ImageGallery
